import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Loader2, Check, Link2 } from 'lucide-react';
import { toast } from 'sonner';

const EditableRiggingTab = ({ event, onUpdate }) => {
  const [rigging, setRigging] = useState(event?.rigging || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const isInitialMount = useRef(true);
  const pendingChanges = useRef({});
  const saveTimeoutRef = useRef(null);

  // Only sync with parent on initial mount
  useEffect(() => {
    if (isInitialMount.current) {
      setRigging(event?.rigging || '');
      isInitialMount.current = false;
    }
  }, [event?.rigging]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Perform the actual save
  const doSave = useCallback(async () => {
    if (Object.keys(pendingChanges.current).length === 0) return;
    
    setSaving(true);
    const changesToSave = { ...pendingChanges.current };
    pendingChanges.current = {};
    
    try {
      await onUpdate(changesToSave);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save rigging:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  }, [onUpdate]);

  // Queue a change with debounce
  const queueChange = useCallback((changes) => {
    pendingChanges.current = { ...pendingChanges.current, ...changes };
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(doSave, 1000);
  }, [doSave]);

  // Save immediately (for blur events)
  const saveNow = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    doSave();
  }, [doSave]);

  const handleChange = (value) => {
    setRigging(value);
    queueChange({ rigging: value });
  };

  return (
    <Card className="border-border/50 bg-card/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Link2 className="h-5 w-5 text-amber-400" />
            Rigging
          </CardTitle>
          <div className="flex items-center gap-2">
            {saving && (
              <Badge variant="outline" className="border-blue-500/30 bg-blue-500/10 text-blue-400">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Saving...
              </Badge>
            )}
            {saved && !saving && (
              <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                <Check className="h-3 w-3 mr-1" />
                Saved
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <Label className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            Rigging Notes & Requirements
          </Label>
          <Textarea
            value={rigging}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={saveNow}
            placeholder="Enter rigging notes and requirements..."
            rows={12}
            className="bg-background/50 resize-y min-h-[200px]"
            data-testid="rigging-notes-textarea"
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default EditableRiggingTab;
